import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { FilePlus2, Layers } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { EmptyState, SectionCard, TableShell, Th } from "../ui";
import { useApplicationRegistry, useApplications, useApplicationsOverview } from "../../hooks";
import { APPLICATION_STATUS_COLORS, APPLICATION_STATUS_LABELS } from "../../types";
import { fmtDate } from "../../utils";
import { BulkCreateApplicationsModal } from "./BulkCreateApplicationsModal";
import { BulkFormatkiAction } from "./BulkFormatkiAction";
import { CreateApplicationModal } from "./CreateApplicationModal";
import { DecisionsPanel } from "./DecisionsPanel";
import { InfoHint } from "./InfoHint";

/**
 * Zbiorcza zakładka „Wnioski” projektu: panel „do obsłużenia” (urzędy bez wniosku
 * lokalizacyjnego, decyzje bez klauzuli), lista pakietów i rejestr decyzji.
 * Pakiety WPW (zgody prywatne, do negocjatora) NIE należą tu — mają własny obieg
 * w zakładce WPW; ta zakładka pokazuje wyłącznie wnioski urzędowe.
 */
export function ApplicationsTab({ connectionId }: { connectionId: string }) {
  const { id: projectId } = useParams<{ id: string }>();
  const { data: registry } = useApplicationRegistry();
  const { data: overview } = useApplicationsOverview(connectionId);
  const { data: applications, isLoading } = useApplications({ connection: connectionId });
  const [modal, setModal] = useState<null | { type?: string; office?: string; decision?: string; parcel?: string }>(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  // Ilu właścicieli instytucjonalnych czeka na wniosek — od tego zależy, czy w ogóle
  // proponujemy zakładanie kompletu. Sprawa prowadzona w Korespondencji liczy się jak
  // wniosek: masówka i tak ją pominie, więc nie wliczamy jej do „brakujących".
  const missingCount = (overview?.office_owners ?? [])
    .filter((o) => !o.existing_application && !o.existing_case).length;
  // Wnioski lokalizacyjne projektu — to dla nich generuje się komplet formatek.
  // Backend i tak zawęża do aktywnych; tu chodzi tylko o to, czy pokazać przycisk.
  const locationApplications = useMemo(
    () => (applications ?? []).filter((a) => a.application_type === "lokalizacyjny"),
    [applications],
  );

  // Pakiety WPW idą do negocjatora — wykluczamy je z zakładki „Wnioski urzędowe".
  const wpwTypeKeys = useMemo(
    () => new Set((registry ?? []).filter((t) => t.recipient_mode === "negotiator").map((t) => t.key)),
    [registry],
  );
  const officeApplications = useMemo(
    () => (applications ?? []).filter((a) => !wpwTypeKeys.has(a.application_type)),
    [applications, wpwTypeKeys],
  );

  // Wniosek o klauzulę bywa wyłączony w rejestrze (obieg nieuzgodniony z biznesem) —
  // wtedy nie proponujemy „Złóż”, bo kreator i tak by odmówił. Lista decyzji bez klauzuli
  // zostaje, tylko kierujemy do rejestru decyzji, gdzie klauzulę odnotowuje się ręcznie.
  const clauseType = (registry ?? []).find((t) => t.key === "klauzula");

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start">
        {/* ── do obsłużenia ── */}
        <SectionCard
          title={
            <span className="flex items-center gap-1">
              Do obsłużenia
              <InfoHint text="System sam wykrywa, co czeka na wniosek: urzędy-właściciele działek bez wniosku lokalizacyjnego (reguła: 1 urząd = 1 wniosek na projekcie) oraz decyzje bez klauzuli ostateczności. Kliknij „Złóż”, żeby założyć pakiet z prefillowanym zakresem. Wniosek wysłany z zakładki Korespondencja (sprawa urzędowa) też się liczy — zamiast „Złóż” widać wtedy sprawę. Działka po złożeniu wniosku „oczekuje na odpowiedź” dopóki sprawa jest otwarta; gdy urząd przekroczy termin KPA, działka jest „po terminie KPA” — do monitowania, nie do ponownego składania. Sprawa zamknięta pozytywnie daje tytuł dostępu i działka jest „pozyskana” — nie wraca do „wymaga akcji”." />
            </span>
          }
        >
          <div className="space-y-3 text-xs">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <div className="section-label flex-1">Urzędy-właściciele (wniosek lokalizacyjny)</div>
                {/* Komplet naraz pokazujemy dopiero, gdy jest co najmniej dwóch czekających
                    właścicieli — przy jednym zwykłe „Złóż” jest krótszą drogą. */}
                {missingCount > 1 && (
                  <Button size="sm" variant="secondary" onClick={() => setBulkOpen(true)}>
                    <Layers size={13} className="mr-1" /> Załóż wszystkie ({missingCount})
                  </Button>
                )}
                {/* Formatki dla kompletu — dla wniosków założonych wcześniej, gdy ekran
                    zakładania jest już zamknięty. */}
                {locationApplications.length > 1 && (
                  <BulkFormatkiAction
                    connectionId={connectionId}
                    label={`Formatki dla kompletu (${locationApplications.length})`}
                  />
                )}
              </div>
              {!overview?.office_owners.length ? (
                <div className="text-[var(--color-text-muted)]">Brak urzędów-właścicieli wśród działek projektu.</div>
              ) : (
                overview.office_owners.map((o) => (
                  <div key={o.office_id} className="flex items-center gap-2 py-0.5">
                    <span className="flex-1 truncate">
                      {o.office_name}{" "}
                      <Badge color="gray">{o.parcel_count} dz.</Badge>{" "}
                      {o.action_parcel_count > 0 && (
                        <Badge color="yellow">{o.action_parcel_count} wymaga akcji</Badge>
                      )}{" "}
                      {/* Wniosek złożony → piłka po stronie urzędu, nie nasz ruch. */}
                      {o.awaiting_parcel_count > 0 && (
                        <Badge color="blue">{o.awaiting_parcel_count} oczekuje na odpowiedź</Badge>
                      )}{" "}
                      {/* Urząd przekroczył termin KPA → nasz ruch to monit, nie drugi wniosek. */}
                      {o.overdue_parcel_count > 0 && (
                        <Badge color="red">{o.overdue_parcel_count} po terminie KPA — monituj</Badge>
                      )}{" "}
                      {/* Sprawa zamknięta pozytywnie → tytuł dostępu jest, działka załatwiona. */}
                      {o.secured_parcel_count > 0 && (
                        <Badge color="green">{o.secured_parcel_count} pozyskane</Badge>
                      )}{" "}
                      {o.additional_parcel_count > 0 && (
                        <Badge color="gray">{o.additional_parcel_count} dodatkowe</Badge>
                      )}
                    </span>
                    {o.existing_application ? (
                      <Link
                        to={`/power-projects/wnioski/${o.existing_application.id}`}
                        className="font-mono hover:underline"
                      >
                        {o.existing_application.number}
                      </Link>
                    ) : o.existing_case ? (
                      /* Sprawa z Korespondencji bez pakietu: wniosek już poszedł (albo
                         urząd już odpowiedział) — pokazujemy sprawę zamiast „Złóż". */
                      <Link
                        to={`/power-projects/${projectId}?tab=correspondence&proceeding=${o.existing_case.id}`}
                        className="hover:underline"
                        title={[
                          o.existing_case.type_name,
                          o.existing_case.our_ref ? `znak ${o.existing_case.our_ref}` : null,
                          o.existing_case.date_sent ? `wysłano ${fmtDate(o.existing_case.date_sent)}` : null,
                          o.existing_case.kpa_deadline ? `termin KPA ${fmtDate(o.existing_case.kpa_deadline)}` : null,
                        ].filter(Boolean).join(" · ")}
                      >
                        <Badge color={
                          o.existing_case.status === "closed" ? "green"
                            : o.existing_case.kpa_overdue ? "red"
                              : o.existing_case.submitted ? "blue" : "yellow"
                        }>
                          {o.existing_case.status === "closed"
                            ? "Załatwione (sprawa)"
                            : o.existing_case.kpa_overdue
                              ? "Po terminie KPA — monituj"
                              : o.existing_case.submitted
                                ? "Sprawa: wniosek wysłany"
                                : "Sprawa w Korespondencji"}
                        </Badge>
                      </Link>
                    ) : (
                      <Button size="sm" variant="secondary"
                              onClick={() => setModal({ type: "lokalizacyjny", office: o.office_id })}>
                        Złóż
                      </Button>
                    )}
                  </div>
                ))
              )}
            </div>
            <div>
              <div className="section-label mb-1">Działki prywatne — pakiety WPW (zgody)</div>
              <div className="text-[var(--color-text-muted)]">
                Zgody prywatne (pakiety WPW) prowadzisz w{" "}
                <Link to={`/power-projects/${projectId}?tab=wpw`} className="text-[var(--color-primary)] hover:underline">
                  zakładce WPW
                </Link>{" "}
                — tam utworzysz pakiety per działka i zapotrzebowanie dla negocjatora.
              </div>
            </div>
            <div>
              <div className="section-label mb-1">Decyzje bez klauzuli ostateczności</div>
              {clauseType && !clauseType.available && !!overview?.decisions_without_clause.length && (
                <div className="mb-1 text-[var(--color-text-muted)]">
                  Wniosek o klauzulę jest jeszcze niedostępny — klauzulę nadaną przez urząd odnotuj
                  stemplem w „Rejestrze decyzji” niżej.
                </div>
              )}
              {!overview?.decisions_without_clause.length ? (
                <div className="text-[var(--color-text-muted)]">Wszystkie decyzje ostateczne (albo brak decyzji).</div>
              ) : (
                overview.decisions_without_clause.map((d) => (
                  <div key={d.id} className="flex items-center gap-2 py-0.5">
                    <span className="flex-1 truncate font-mono">{d.sign}</span>
                    <span className="text-[var(--color-text-muted)]">{d.issuing}</span>
                    {d.has_active_application ? (
                      <Badge color="blue">wniosek w toku</Badge>
                    ) : clauseType?.available ? (
                      <Button size="sm" variant="secondary"
                              onClick={() => setModal({ type: "klauzula", decision: d.id })}>
                        Złóż o klauzulę
                      </Button>
                    ) : null}
                  </div>
                ))
              )}
            </div>
          </div>
        </SectionCard>

        {/* ── lista pakietów ── */}
        <SectionCard
          title={
            <span className="flex items-center gap-1">
              Wnioski urzędowe
              <InfoHint text="Wszystkie pakiety wniosków projektu. Każdy pakiet prowadzi Cię fazami (zakres → pełnomocnictwa i opłaty → dokument → mapy → załączniki → kompilacja → wysyłka); w każdej fazie masz pełną kontrolę i możesz cofnąć się do edycji. Kolumna postępu pokazuje zatwierdzone fazy." />
            </span>
          }
          action={
            <Button size="sm" onClick={() => setModal({})}>
              <FilePlus2 size={13} className="mr-1" /> Nowy wniosek
            </Button>
          }
        >
          {isLoading ? null : !officeApplications.length ? (
            <EmptyState>Brak wniosków urzędowych — utwórz pierwszy pakiet.</EmptyState>
          ) : (
            <TableShell>
              <thead>
                <tr>
                  <Th>Numer</Th><Th>Typ</Th><Th>Adresat</Th><Th>Fazy</Th><Th>Status</Th><Th>Utworzono</Th>
                </tr>
              </thead>
              <tbody>
                {officeApplications.map((a) => (
                  <tr key={a.id} className="border-t border-[var(--color-border)] hover:bg-[var(--color-bg-elevated)]">
                    <td className="px-2 py-1.5 font-mono">
                      <Link to={`/power-projects/wnioski/${a.id}`} className="hover:underline font-bold">
                        {a.number}
                      </Link>
                    </td>
                    <td className="px-2 py-1.5">{a.type_label}</td>
                    <td className="px-2 py-1.5">{a.recipient_name || "—"}</td>
                    <td className="px-2 py-1.5 font-mono">
                      {a.phase_progress.approved}/{a.phase_progress.total}
                    </td>
                    <td className="px-2 py-1.5">
                      <Badge color={APPLICATION_STATUS_COLORS[a.status]}>
                        {APPLICATION_STATUS_LABELS[a.status]}
                      </Badge>
                    </td>
                    <td className="px-2 py-1.5">{fmtDate(a.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </TableShell>
          )}
        </SectionCard>
      </div>

      <DecisionsPanel connectionId={connectionId} projectId={projectId} />

      {modal && (
        <CreateApplicationModal
          connectionId={connectionId}
          presetType={modal.type}
          presetOffice={modal.office}
          presetDecision={modal.decision}
          presetParcel={modal.parcel}
          onClose={() => setModal(null)}
          onBulk={() => { setModal(null); setBulkOpen(true); }}
        />
      )}
      {bulkOpen && (
        <BulkCreateApplicationsModal
          connectionId={connectionId}
          onClose={() => setBulkOpen(false)}
        />
      )}
    </div>
  );
}
